import { useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { FigureLine, PeriodFigures } from '~/team_tools/dashboard/statistic/figures'
import { usePeriod, useWindowParams } from '~/team_tools/dashboard/statistic/period'
import { Box, Chart, Header, Stat, StatLabel, StatNumber, dayLabel } from '~/shared/ui'
import { useZone } from '~/team_tools/components/timezone'

type PeriodSales = {
    usd: number | null
    usd_orders: number
    points: number | null
    points_orders: number
}

type StoreSales = PeriodSales & {
    /** False where the store app is not part of the deployment (everywhere but production). */
    available: boolean
    periods: Record<string, PeriodSales>
}

// `today`/`week`/`month` are to-date from midnight, Monday and the 1st; the
// `last N` windows are rolling from now. Both are shown because they answer
// different questions, and the labels have to make that difference visible --
// "this week" and "last 7 days" are otherwise read as the same number.
const PERIODS: [key: string, label: string][] = [
    ['today', 'Today'],
    ['week', 'This week'],
    ['month', 'This month'],
    ['24_hours', 'Last 24 hours'],
    ['7_days', 'Last 7 days'],
    ['30_days', 'Last 30 days'],
]

const PERIOD_KEYS = PERIODS.map(([key]) => key)

// 31: enough for the longest month, which is the widest window.
const CHART_DAYS = 31

// Each line is keyed to its own axis by colour, since they read against
// different scales and the legend alone will not make that clear.
const USD_COLOUR = 'rgb(34, 197, 94)'
const POINTS_COLOUR = '#eab308'

// Money keeps its cents; POINTS are whole and would read as noise with them.
const formatUsd = (value: number) =>
    `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const formatPoints = (value: number) => value.toLocaleString('en-US', { maximumFractionDigits: 0 })

const DOLLAR_AXIS = {
    scales: {
        y: {
            min: 0,
            ticks: { maxTicksLimit: 5, font: { size: 10 }, callback: (value: number) => `$${value}` },
            grid: { color: 'rgba(255,255,255,0.06)' },
        },
    },
}

const PeriodGrid = ({
    sales,
    field,
    ordersField,
    format,
    line,
}: {
    sales?: StoreSales
    field: 'usd' | 'points'
    ordersField: 'usd_orders' | 'points_orders'
    format: (value: number) => string
    line?: FigureLine
}) => (
    <PeriodFigures
        chart={line && { lines: [line], options: field === 'usd' ? DOLLAR_AXIS : undefined }}
        // All time on the dashboard; elsewhere the lifetime tile above says it.
        total={
            usePeriod() && sales?.[field] != null
                ? {
                      label: 'All time',
                      value: format(sales[field] as number),
                      note: `${sales[ordersField]} orders`,
                  }
                : undefined
        }
        periods={PERIODS}
        loading={sales === undefined}
        value={(key) => {
            const value = sales?.periods?.[key]?.[field]
            return value != null ? format(value) : undefined
        }}
        note={(key) => {
            const period = sales?.periods?.[key]
            return period ? `${period[ordersField]} orders` : undefined
        }}
    />
)

export const StoreSalesTotals = () => {
    const [sales, setSales] = useState<StoreSales>()
    const [labels, setLabels] = useState<string[]>([])
    const [usdHistory, setUsdHistory] = useState<number[]>([])
    const [pointsHistory, setPointsHistory] = useState<number[]>([])
    const [history, setHistory] = useState<Record<string, [number, number]>>({})
    const period = usePeriod()
    const window = useWindowParams()

    // The windows and chart days are days in the zone the shell names.
    const [zone] = useZone()

    const getData = () => {
        let live = true
        jsonrpc('statistic.store.sales', {
            periods: PERIOD_KEYS.concat(window.custom ? ['custom'] : []),
            tz: zone,
            ...window.figure,
        }).then((res) => live && setSales(res.result))
        setHistory({})
        jsonrpc(
            'statistic.store.sales.history',
            period ? { ...window.history, tz: zone } : { days: CHART_DAYS, tz: zone }
        ).then((res) => {
            if (!live) return
            const history = res.result?.history || {}
            setHistory(history)
            setLabels(Object.keys(history).map(dayLabel))
            setUsdHistory(Object.values(history).map(([usd]: [number, number]) => usd))
            setPointsHistory(Object.values(history).map(([, points]: [number, number]) => points))
        })
        return () => {
            live = false
        }
    }

    useEffect(() => getData(), [zone, window.key])

    const unavailable = sales?.available === false

    return (
        <Box className='w-full text-center gap-2'>
            <Header>Store Sales</Header>
            <small className='text-white/50'>
                {unavailable
                    ? 'The store is not enabled on this environment, so there are no sales to report here.'
                    : 'Paid store orders, totalled by the currency the buyer paid in. Gross: a reversed or undelivered order still counts, and fraud-tagged buyers are included.'}
            </small>

            <Box className='w-full gap-2 pt-2'>
                {!period && (
                    <Stat.Gray className='w-full' loading={sales === undefined}>
                        <StatLabel className='text-amber-500'>USD (USDC) — all time</StatLabel>
                        <StatNumber>{sales?.usd != null ? formatUsd(sales.usd) : undefined}</StatNumber>
                        <StatLabel className='text-white/40'>
                            {sales?.usd != null ? `${sales.usd_orders} orders` : undefined}
                        </StatLabel>
                    </Stat.Gray>
                )}
                <PeriodGrid
                    sales={sales}
                    field='usd'
                    ordersField='usd_orders'
                    format={formatUsd}
                    line={{
                        label: 'USD (USDC)',
                        colour: USD_COLOUR,
                        history: Object.fromEntries(Object.entries(history).map(([day, [usd]]) => [day, usd])),
                    }}
                />
            </Box>

            <Box className='w-full gap-2 pt-4'>
                {!period && (
                    <Stat.Gray className='w-full' loading={sales === undefined}>
                        <StatLabel className='text-amber-500'>POINTS — all time</StatLabel>
                        <StatNumber>{sales?.points != null ? formatPoints(sales.points) : undefined}</StatNumber>
                        <StatLabel className='text-white/40'>
                            {sales?.points != null ? `${sales.points_orders} orders` : undefined}
                        </StatLabel>
                    </Stat.Gray>
                )}
                <PeriodGrid
                    sales={sales}
                    field='points'
                    ordersField='points_orders'
                    format={formatPoints}
                    line={{
                        label: 'POINTS',
                        colour: POINTS_COLOUR,
                        history: Object.fromEntries(Object.entries(history).map(([day, [, points]]) => [day, points])),
                    }}
                />
            </Box>

            {/* One chart, two lines, two y-axes. Dollars and points differ by orders of
                magnitude (hundreds against hundreds of thousands), so a shared axis would
                flatten USD onto the floor. Each line is read against its own side: USD left
                in green, POINTS right in yellow, with the axis labels coloured to match so
                it is obvious which scale belongs to which line. */}
            {/* On the dashboard each figure draws its own window above; this
                fixed chart is for callers with no window. */}
            {!unavailable && !period && (
                <Box className='w-full gap-2 pt-4'>
                    <small className='text-white/50'>Daily store purchases, last {CHART_DAYS} days</small>
                    <Box.Gray className='w-full'>
                        <Chart.TimeSeries
                            className='h-48 sm:h-56'
                            data={{
                                labels,
                                datasets: [
                                    {
                                        label: 'USD (USDC)',
                                        data: [...usdHistory],
                                        borderColor: USD_COLOUR,
                                        backgroundColor: USD_COLOUR,
                                        yAxisID: 'y',
                                    },
                                    {
                                        label: 'POINTS',
                                        data: [...pointsHistory],
                                        borderColor: POINTS_COLOUR,
                                        backgroundColor: POINTS_COLOUR,
                                        yAxisID: 'y1',
                                    },
                                ],
                            }}
                            options={{
                                scales: {
                                    y: {
                                        position: 'left',
                                        min: 0,
                                        ticks: {
                                            maxTicksLimit: 5,
                                            font: { size: 10 },
                                            color: USD_COLOUR,
                                            callback: (value: number) => `$${value}`,
                                        },
                                        grid: { color: 'rgba(255,255,255,0.06)' },
                                    },
                                    y1: {
                                        position: 'right',
                                        min: 0,
                                        ticks: {
                                            precision: 0,
                                            maxTicksLimit: 5,
                                            font: { size: 10 },
                                            color: POINTS_COLOUR,
                                        },
                                        // Only one set of gridlines, or the two axes draw
                                        // interleaved lines that belong to neither scale.
                                        grid: { drawOnChartArea: false },
                                    },
                                },
                            }}
                        />
                    </Box.Gray>
                </Box>
            )}
        </Box>
    )
}
